import { HttpStatusCode } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { LanguageTextboxComponent } from '@components/language-textbox/language-textbox.component';
import {
    PageTitle,
    SearchChannelGroupComponent,
    SelectedChannel,
} from '@components/search-channel-group/search-channel-group.component';
import { ScreenSizeType } from '@models/responsive';
import { TranslateService } from '@ngx-translate/core';
import { CustomGroupAccessSettingsService } from '@service/custom-group-access-settings.service';
import { CustomGroupsService } from '@service/custom-groups.service';
import { LanguageService } from '@service/language.service';
import { ResponsiveService } from '@service/responsive.service';
import { ToastrService } from 'ngx-toastr';
import { CustomGroup } from 'ops/common/models/custom-group';
import { LeavePageConfirmationComponent } from '@components/leave-page-confirmation/leave-page-confirmation.component';
import { first, firstValueFrom, lastValueFrom, skip, take } from 'rxjs';
import lodash from 'lodash';
import { AuthService } from '@service/auth.service';
import { CUSTOM_GROUP_MAX_CHANNELS } from 'ops/common/validators/custom-group/custom-group-validator';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { Channel } from 'ops/common/models/channel';

export interface groupListItem {
    displayName: string;
    checked: Boolean;
    id: string;
}

@Component({
    selector: 'app-ops-new-custom-group',
    templateUrl: './ops-new-custom-group.component.html',
    styleUrls: ['./ops-new-custom-group.component.scss'],
    standalone: false,
})
export class OpsNewCustomGroupComponent implements OnInit, OnDestroy {
    @Input() editData;
    @Input() customGroupNumber;
    @Input() channels: Channel[];
    @Input() projectCustomGroups: CustomGroup[];
    @Output() onGoBack: EventEmitter<String> = new EventEmitter();
    @Output() onSubmitCustomGroup: EventEmitter<String> = new EventEmitter();
    @ViewChild('searchChannelComponent')
    searchChannelComponent: SearchChannelGroupComponent;
    @ViewChild('exitConfirmOverlay') exitConfirmOverlay: LeavePageConfirmationComponent;
    @ViewChild('languageInput') languageInput: LanguageTextboxComponent;
    @ViewChild('conflictNotification') conflictNotification: PopUpNotificationComponent;

    readonly MAX_CHANNELS: number = CUSTOM_GROUP_MAX_CHANNELS;

    newGroupName: string;

    newGroupDescription: string = '';
    assignedLanguages: Set<string> = new Set();
    languageInputActive: boolean = false;

    channelsSelected = [];
    selectingChannels: boolean = false;
    editingTitle: Boolean = false;
    mouseDownBinded = this.checkMouseClick.bind(this);
    editChannels: SelectedChannel[];
    previewChannels: Channel[] = [];
    isPublicGroup: boolean;
    usersAccess = [];
    accessHasChanged: boolean = false;
    titleError: string;
    isSubmiting: boolean = false;
    displaySuggestions: boolean = false;

    private initalUsersAccess;
    private isLoadingChannels: boolean = false;
    private isLoading: boolean = false;

    readonly pageTitle: PageTitle = PageTitle.ChannelGroup;
    readonly showPreviewScreen = new Set([
        ScreenSizeType.Medium,
        ScreenSizeType.Small,
        ScreenSizeType.XSmall,
    ]);

    constructor(
        private toastr: ToastrService,
        private customGroupsService: CustomGroupsService,
        public languageService: LanguageService,
        public responsive: ResponsiveService,
        private cdr: ChangeDetectorRef,
        private customGroupAccessService: CustomGroupAccessSettingsService,
        private translateService: TranslateService,
        private authService: AuthService,
    ) {}

    async ngOnInit() {
        this.newGroupName = 'Custom channel group ' + this.customGroupNumber;
        this.isPublicGroup = true;
        this.isLoadingChannels = true;
        this.isLoading = true;
        if (this.editData) {
            this.newGroupName = this.editData.groupName;
            this.isPublicGroup = this.editData.publicAccess;
            this.editChannels = this.editData.channels.map((channel) => {
                return {
                    name: channel.opsData.name,
                    channelId: channel.id,
                    fcuChannelId: channel.fcuData.fcuChannelId,
                };
            });
            this.usersAccess = await this.customGroupAccessService.getAll(this.editData.id);
            this.initalUsersAccess = lodash?.cloneDeep(this.usersAccess);
            this.newGroupDescription = this.editData.description;
        }

        this.responsive.breakPoinEmitter.subscribe((breakPoint) => {
            if (!this.showPreviewScreen.has(breakPoint)) {
                this.onPreviewBack();
                this.cdr.markForCheck();
            }
        });
        this.isLoading = false;
        this.cdr.markForCheck();
    }

    ngOnDestroy() {
        this.isSubmiting = false;
    }

    hasFinishedLoading() {
        return !this.isLoading && !this.isLoadingChannels;
    }

    onLoadingCompleted() {
        this.isLoadingChannels = false;
    }

    deleteChanges() {
        this.newGroupName = '';
        this.newGroupDescription = '';
        if (this.editData) {
            this.onGoBack.emit(this.editData.groupId);
        } else {
            this.onGoBack.emit();
        }
    }

    checkName(e) {
        if (!e.target.value || e.target.value.trim().length === 0) {
            this.titleError = this.translateService.instant(
                'custom-group.create.error.title.empty',
            );
        }
    }

    async onDiscard() {
        if (!this.hasUnsavedChanges()) {
            this.deleteChanges();
            return;
        }
        this.exitConfirmOverlay.setVisible(true);
        const leavePage = await lastValueFrom(
            this.exitConfirmOverlay.onOptionSelected.pipe(take(1)),
        );
        if (leavePage) {
            this.deleteChanges();
        }
    }

    async tryCloseCreatePage() {
        if (this.isSubmiting) {
            this.isSubmiting = false;
            return true;
        }
        this.exitConfirmOverlay.setVisible(true);
        const leavePage = await lastValueFrom(
            this.exitConfirmOverlay.onOptionSelected.pipe(take(1)),
        );
        this.isSubmiting = false;
        return leavePage;
    }

    public hasUnsavedChanges(): boolean {
        if (this.isSubmiting) {
            return false;
        } else if (!this.editChannels && this.channelsSelected.length !== 0) {
            return true;
        } else if (this.editData) {
            if (
                !lodash.isEqual(this.editChannels, this.channelsSelected) ||
                !lodash.isEqual(this.editData.groupName, this.newGroupName) ||
                !lodash.isEqual(this.initalUsersAccess, this.usersAccess) ||
                !lodash.isEqual(this.editData.publicAccess, this.isPublicGroup) ||
                !lodash.isEqual(this.editData.description, this.newGroupDescription)
            ) {
                return true;
            }
        } else if (
            !this.editData &&
            (this.newGroupName !== 'Custom channel group ' + this.customGroupNumber ||
                !this.isPublicGroup)
        ) {
            return true;
        }
        return false;
    }

    onSelectedChannelsChanged(channels) {
        this.channelsSelected = channels;
    }

    editTitle() {
        document.addEventListener('mousedown', this.mouseDownBinded);
        this.editingTitle = true;
    }

    handleTableDisplay(selectingChannels) {
        this.selectingChannels = selectingChannels;
    }

    async onSubmit() {
        if (this.isSubmiting) {
            return;
        }
        this.isSubmiting = true;
        if (this.newGroupName.trim().length === 0) {
            this.toastr.error('Please write a group name', '', {
                timeOut: 3000,
                closeButton: true,
            });
        } else if (this.channelsSelected.length === 0) {
            this.toastr.error('Please select channels', '', {
                timeOut: 3000,
                closeButton: true,
            });
        } else if (!this.editData) {
            const groupData = {
                groupName: this.newGroupName,
                channels: this.channelsSelected,
                description: this.newGroupDescription,
                publicAccess: this.isPublicGroup,
            };

            try {
                const { newGroupId } = await this.customGroupsService.postGroup(groupData);
                await this.customGroupAccessService.create(this.usersAccess, newGroupId);
                this.onSubmitCustomGroup.emit(newGroupId);
                this.newGroupName = '';
                this.newGroupDescription = '';
            } catch (err) {
                this.isSubmiting = false;
                if (err.status == HttpStatusCode.Unauthorized) {
                    this.authService.signOut();
                    this.authService.showSignInOverlay();
                    const loggedInUser = await firstValueFrom(
                        this.authService.currentUser$.pipe(skip(1), first()),
                    );
                    if (loggedInUser.token && loggedInUser.userId) {
                        this.onSubmit();
                    }
                } else if (err.status == HttpStatusCode.Conflict) {
                    this.conflictNotification.setVisible(true);
                    console.error(err);
                } else {
                    this.deleteChanges();
                    console.error(err);
                }
            }
        } else {
            const groupData = {
                id: this.editData.groupId,
                groupName: this.newGroupName,
                channels: this.channelsSelected,
                description: this.newGroupDescription,
                publicAccess: this.isPublicGroup,
            };
            try {
                await this.customGroupsService.updateGroup(groupData);
                await this.customGroupAccessService.update(this.usersAccess);
                this.onSubmitCustomGroup.emit(this.editData.groupId);
                this.newGroupName = '';
                this.newGroupDescription = '';
            } catch (err) {
                this.isSubmiting = false;
                console.error(err);
                this.deleteChanges();
            }
        }
    }

    checkRequiredConditions() {
        return (
            !this.hasUnsavedChanges() ||
            this.newGroupName.trim().length === 0 ||
            this.channelsSelected.length === 0 ||
            this.titleIsRepeated()
        );
    }

    checkMouseClick(e) {
        if (this.editingTitle) {
            if (e.target.id != 'input-bar') {
                this.editingTitle = false;
                document.removeEventListener('mousedown', this.mouseDownBinded);
            }
        }
    }

    removeChannel(channel) {
        this.searchChannelComponent.unselectChannel(channel.channelId);
    }

    onShowPreviewChannelList() {
        this.previewChannels = [];
        for (let i = 0; i < this.channelsSelected.length; i++) {
            for (let j = 0; j < this.channels.length; j++) {
                if (this.channels[j].id == this.channelsSelected[i].channelId) {
                    this.previewChannels[i] = this.channels[j];
                }
            }
        }
    }

    onPreviewBack() {
        this.previewChannels = [];
    }

    editDescription(description: string) {
        this.newGroupDescription = description;
    }

    showPreview() {
        return this.showPreviewScreen.has(this.responsive.screenSize);
    }

    handleAccessChanged(isPublic) {
        this.isPublicGroup = isPublic;
    }

    handleUserAccessChanged(newUsersPermissions) {
        if (this.usersAccess.length > 0) {
            this.usersAccess.map((access) => {
                if (access.roleId in newUsersPermissions) {
                    access.accessLevel = newUsersPermissions[access.roleId];
                }
            });
        } else {
            for (let user in newUsersPermissions) {
                this.usersAccess.push({
                    roleId: user,
                    accessLevel: newUsersPermissions[user],
                });
            }
        }
    }

    titleIsRepeated() {
        let hasError = false;
        if (this.projectCustomGroups) {
            this.projectCustomGroups.map((customGroup) => {
                if (customGroup.displayName === this.newGroupName) {
                    if (this.editData) {
                        if (customGroup.id !== this.editData.id) {
                            hasError = true;
                            this.titleError = this.translateService.instant(
                                'custom-group.create.error.title.repeat',
                            );
                        }
                    } else {
                        hasError = true;
                        this.titleError = this.translateService.instant(
                            'custom-group.create.error.title.repeat',
                        );
                    }
                }
            });
        }
        return hasError;
    }

    getTitleList(): string[] {
        return this.projectCustomGroups.map((customGroup) => {
            return customGroup.displayName;
        });
    }
}
